import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken, Store } from '@ngxs/store';
import { EMPTY, map, Observable } from 'rxjs';
import { FavoriteTestsApiService } from 'src/app/services/favorite-tests-api.service';
import { Logout } from 'src/modules/security/actions/auth-actions';
import { CurrentAccountState } from 'src/states';
import { CurrentAccountRestored, SetCurrentAccountLocation } from 'src/states/current-account/current-account.actions';
import { FavoriteTest } from '../models/favorite-test.model';
import { AddFavoriteTest, FetchFavoriteTests } from './actions/favorite-tests.actions';
import { Test } from 'src/shared/models/test.model';
import { TestTypes } from 'src/shared/enums/test-types.enum';
import { AddTests } from './actions/order.actions';
import { LabSelectionChanged } from '../components/order-info/states/actions/order-info-step.actions';
import { LabSelectionByFeatureChanged } from 'src/app/states/actions/lab-selection.actions';
import { sortByProperty } from 'src/app/utils/sort-by-property.util';
import { convertTestDtoToTest } from 'src/app/utils/convert-test-dto-to-test.util';

interface FavoriteTestsStateModel {
  status: 'notLoaded' | 'loading' | 'loaded';
  practiceId: number | null;
  tests: FavoriteTest[];
}

const defaults: FavoriteTestsStateModel = {
  status: 'notLoaded',
  practiceId: null,
  tests: []
};

export const FAVORITE_TESTS_STATE_TOKEN = new StateToken<FavoriteTestsStateModel>('favoriteTests');

@State<FavoriteTestsStateModel>({
  name: FAVORITE_TESTS_STATE_TOKEN,
  defaults
})
@Injectable()
export class FavoriteTestsState {
  private readonly store = inject(Store);
  private readonly apiService = inject(FavoriteTestsApiService);

  @Selector()
  public static loaded(state: FavoriteTestsStateModel): boolean {
    return state.status === 'loaded';
  }

  @Selector()
  public static tests(state: FavoriteTestsStateModel): FavoriteTest[] {
    return sortByProperty(state.tests, 'description');
  }

  @Action(FetchFavoriteTests)
  public fetchFavoriteTests(ctx: StateContext<FavoriteTestsStateModel>, { laboratoryFacilityId }: FetchFavoriteTests): Observable<void> {
    const practiceId = this.store.selectSnapshot(CurrentAccountState.practiceId);

    if (!practiceId) {
      console.error('practiceId is empty');
      return EMPTY;
    }

    ctx.patchState({ practiceId: practiceId, status: 'loading' });

    return this.apiService
      .getFavoriteTests(practiceId, laboratoryFacilityId)
      .pipe(map(result => ctx.patchState({ status: 'loaded', tests: result })));
  }

  @Action([CurrentAccountRestored, SetCurrentAccountLocation, Logout, LabSelectionChanged, LabSelectionByFeatureChanged])
  public clearState(ctx: StateContext<FavoriteTestsStateModel>): void {
    ctx.setState(defaults);
  }

  @Action(AddFavoriteTest)
  public addFavoriteTest(
    ctx: StateContext<FavoriteTestsStateModel>,
    { payload, shouldProfilesExplode }: AddFavoriteTest
  ): Observable<void> {
    const isProfile = payload.type === TestTypes.Profile;

    if (shouldProfilesExplode && isProfile && payload.components?.length) {
      // Profile explosion is handled inline by the UI — no action needed here
      return EMPTY;
    }

    const test: Test = {
      id: payload.id,
      code: payload.code,
      description: payload.description,
      components: (payload.components ?? []).map(convertTestDtoToTest),
      isTypeProfile: isProfile,
      type: payload.type
    };
    return ctx.dispatch(new AddTests([test]));
  }
}
